/**
 * Transaction Field Mapping
 */

import { FieldMap } from './types';

/**
 * Transaction Field Map
 * 
 * UI Field           | Parse V1              | Parse V2
 * -------------------|----------------------|------------------------
 * id                 | objectId             | objectId
 * memberId           | member_id            | member.objectId
 * memberUsername     | member_username      | member.username
 * type               | type                 | transactionType
 * amount             | amount               | amount
 * fee                | fee                  | fees.transaction
 * netAmount          | net_amount           | netAmount
 * status             | status               | status
 * bankAccountId      | bank_account_id      | bankAccount.objectId
 * bankName           | bank_name            | bankAccount.bankName
 * accountNumber      | account_number       | bankAccount.accountNumber
 * reference          | reference            | reference
 * note               | note                 | notes
 * processedBy        | processed_by         | processedBy.objectId
 * processedAt        | processed_at         | processedAt
 * createdAt          | createdAt            | createdAt
 * updatedAt          | updatedAt            | updatedAt
 */
export const transactionFieldMap: FieldMap = {
  id: {
    uiField: 'id',
    parseV1Field: 'objectId',
    parseV2Field: 'objectId',
    required: true,
  },
  memberId: {
    uiField: 'memberId',
    parseV1Field: 'member_id',
    parseV2Field: 'member.objectId',
    required: true,
  },
  memberUsername: {
    uiField: 'memberUsername',
    parseV1Field: 'member_username',
    parseV2Field: 'member.username',
  },
  type: {
    uiField: 'type',
    parseV1Field: 'type',
    parseV2Field: 'transactionType',
    required: true,
  },
  amount: {
    uiField: 'amount',
    parseV1Field: 'amount',
    parseV2Field: 'amount',
    required: true,
    fromParse: (value) => Number(value) || 0,
  },
  fee: {
    uiField: 'fee',
    parseV1Field: 'fee',
    parseV2Field: 'fees.transaction',
    defaultValue: 0,
    fromParse: (value) => Number(value) || 0,
  },
  netAmount: {
    uiField: 'netAmount',
    parseV1Field: 'net_amount',
    parseV2Field: 'netAmount',
    fromParse: (value) => Number(value) || 0,
  },
  status: {
    uiField: 'status',
    parseV1Field: 'status',
    parseV2Field: 'status',
    required: true,
    fromParse: (value, version) => {
      if (version === 'v1') {
        const statusMap: Record<string, string> = {
          '0': 'pending',
          '1': 'approved',
          '2': 'rejected',
          '3': 'processing',
          '4': 'completed',
          '-1': 'failed',
        };
        return statusMap[String(value)] || value;
      }
      return value;
    },
  },
  bankAccountId: {
    uiField: 'bankAccountId',
    parseV1Field: 'bank_account_id',
    parseV2Field: 'bankAccount.objectId',
  },
  bankName: {
    uiField: 'bankName',
    parseV1Field: 'bank_name',
    parseV2Field: 'bankAccount.bankName',
  },
  accountNumber: {
    uiField: 'accountNumber',
    parseV1Field: 'account_number',
    parseV2Field: 'bankAccount.accountNumber',
  },
  reference: {
    uiField: 'reference',
    parseV1Field: 'reference',
    parseV2Field: 'reference',
  },
  note: {
    uiField: 'note',
    parseV1Field: 'note',
    parseV2Field: 'notes',
  },
  processedBy: {
    uiField: 'processedBy',
    parseV1Field: 'processed_by',
    parseV2Field: 'processedBy.objectId',
  },
  processedAt: {
    uiField: 'processedAt',
    parseV1Field: 'processed_at',
    parseV2Field: 'processedAt',
    fromParse: (value) => value ? new Date(value as string).toISOString() : undefined,
  },
  createdAt: {
    uiField: 'createdAt',
    parseV1Field: 'createdAt',
    parseV2Field: 'createdAt',
    fromParse: (value) => value ? new Date(value as string).toISOString() : undefined,
  },
  updatedAt: {
    uiField: 'updatedAt',
    parseV1Field: 'updatedAt',
    parseV2Field: 'updatedAt',
    fromParse: (value) => value ? new Date(value as string).toISOString() : undefined,
  },
};

/**
 * Slip Field Map
 */
export const slipFieldMap: FieldMap = {
  id: {
    uiField: 'id',
    parseV1Field: 'objectId',
    parseV2Field: 'objectId',
    required: true,
  },
  transRef: {
    uiField: 'transRef',
    parseV1Field: 'trans_ref',
    parseV2Field: 'transactionRef',
    required: true,
  },
  senderName: {
    uiField: 'senderName',
    parseV1Field: 'sender_name',
    parseV2Field: 'sender.name',
  },
  senderBank: {
    uiField: 'senderBank',
    parseV1Field: 'sender_bank',
    parseV2Field: 'sender.bank',
  },
  senderAccount: {
    uiField: 'senderAccount',
    parseV1Field: 'sender_account',
    parseV2Field: 'sender.accountNumber',
  },
  receiverName: {
    uiField: 'receiverName',
    parseV1Field: 'receiver_name',
    parseV2Field: 'receiver.name',
  },
  receiverBank: {
    uiField: 'receiverBank',
    parseV1Field: 'receiver_bank',
    parseV2Field: 'receiver.bank',
  },
  receiverAccount: {
    uiField: 'receiverAccount',
    parseV1Field: 'receiver_account',
    parseV2Field: 'receiver.accountNumber',
  },
  amount: {
    uiField: 'amount',
    parseV1Field: 'amount',
    parseV2Field: 'amount',
    required: true,
    fromParse: (value) => Number(value) || 0,
  },
  transferDate: {
    uiField: 'transferDate',
    parseV1Field: 'transfer_date',
    parseV2Field: 'transferDate',
    fromParse: (value) => value ? new Date(value as string).toISOString() : undefined,
  },
  uploadDate: {
    uiField: 'uploadDate',
    parseV1Field: 'upload_date',
    parseV2Field: 'uploadedAt',
    fromParse: (value) => value ? new Date(value as string).toISOString() : undefined,
  },
  source: {
    uiField: 'source',
    parseV1Field: 'source',
    parseV2Field: 'source',
  },
  status: {
    uiField: 'status',
    parseV1Field: 'status',
    parseV2Field: 'verificationStatus',
  },
  matchedMemberId: {
    uiField: 'matchedMemberId',
    parseV1Field: 'matched_member_id',
    parseV2Field: 'matchedMember.objectId',
  },
  matchedMemberUsername: {
    uiField: 'matchedMemberUsername',
    parseV1Field: 'matched_member_username',
    parseV2Field: 'matchedMember.username',
  },
  imageUrl: {
    uiField: 'imageUrl',
    parseV1Field: 'image_url',
    parseV2Field: 'slipImage.url',
  },
};

/**
 * Parse Class Names
 */
export const TRANSACTION_CLASS_NAME = {
  v1: 'Transaction',
  v2: 'Transaction',
} as const;

export const SLIP_CLASS_NAME = {
  v1: 'Slip',
  v2: 'SlipRecord',
} as const;













